/**
 * TroubleshooterDiffView.tsx — Phase 2 Mode B: before/after diff table
 *
 * Displays per-line audit results from POST /mgmt/reprocess-invoice-audit.
 * Each row is colour-coded by status:
 *   resolved / improved → green
 *   changed (confidence < 0.8) → amber
 *   unresolved           → red
 *   unchanged            → grey
 *
 * A tooltip on each row shows the audit_explanation from the AI.
 * Counterfactuals appear as a collapsed section below the diff table.
 * "Promote All Suggestions" button opens PromoteToVendorDialog (parent manages
 * the dialog — this component calls onPromote with the promotion_items list).
 *
 * Phase 1 (Mode A) remains unchanged — this component is only shown in Mode B.
 */

import React, { useState } from 'react';
import type { AuditResponse, AuditLine, Counterfactual, PromotionItem } from '../adminTypes';

// ── Constants ─────────────────────────────────────────────────────────────────

const NAVY    = '#00386C';
const GREEN   = '#d4edda';
const RED     = '#f8d7da';
const AMBER   = '#fff3cd';
const GREY    = '#f8f9fa';
const GREEN_T = '#155724';
const RED_T   = '#721c24';
const AMBER_T = '#856404';
const GREY_T  = '#495057';

// ── Helpers ───────────────────────────────────────────────────────────────────

function rowColor(status: AuditLine['status']): { bg: string; text: string } {
  switch (status) {
    case 'resolved':
    case 'improved':   return { bg: GREEN, text: GREEN_T };
    case 'changed':    return { bg: AMBER, text: AMBER_T };
    case 'unresolved': return { bg: RED,   text: RED_T   };
    default:           return { bg: GREY,  text: GREY_T  };
  }
}

function statusLabel(status: AuditLine['status']): string {
  switch (status) {
    case 'resolved':   return '✅ Resolved';
    case 'improved':   return '✅ Improved';
    case 'changed':    return '⚠️ Changed';
    case 'unresolved': return '❌ Unresolved';
    default:           return '— Unchanged';
  }
}

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

// ── Diff row ──────────────────────────────────────────────────────────────────

function DiffRow({ line }: { line: AuditLine }) {
  const [tipVisible, setTipVisible] = useState(false);
  const { bg, text } = rowColor(line.status);

  const glChanged =
    (line.audit_gl || '') !== (line.original_gl || '') &&
    line.status !== 'unchanged';

  return (
    <tr
      style={{ backgroundColor: bg, color: text, cursor: 'help', position: 'relative' }}
      onMouseEnter={() => setTipVisible(true)}
      onMouseLeave={() => setTipVisible(false)}
    >
      <td style={tdStyle}>{line.line_number}</td>
      <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={line.description}>
        {line.description}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {line.amount != null ? `$${Number(line.amount).toFixed(2)}` : '—'}
      </td>
      <td style={tdStyle}>
        <span style={{ textDecoration: glChanged ? 'line-through' : 'none', opacity: 0.7 }}>
          {line.original_gl || <em>unresolved</em>}
        </span>
      </td>
      <td style={tdStyle}>
        <strong>{line.audit_gl || <em>unresolved</em>}</strong>
        {line.audit_confidence > 0 && (
          <span style={{ marginLeft: '6px', fontSize: '11px', opacity: 0.75 }}>
            {pct(line.audit_confidence)}
          </span>
        )}
      </td>
      <td style={tdStyle}>{statusLabel(line.status)}</td>

      {/* Tooltip */}
      {tipVisible && line.audit_explanation && (
        <td style={{ padding: 0, border: 'none', position: 'relative' }}>
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            zIndex: 200,
            backgroundColor: '#333',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            maxWidth: '320px',
            whiteSpace: 'normal',
            lineHeight: '1.4',
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}>
            {line.audit_explanation}
          </div>
        </td>
      )}
    </tr>
  );
}

const tdStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  fontSize: '12px',
  verticalAlign: 'middle',
};

const thStyle: React.CSSProperties = {
  ...tdStyle,
  backgroundColor: NAVY,
  color: 'white',
  fontWeight: 600,
  textAlign: 'left',
};

// ── Counterfactual section ────────────────────────────────────────────────────

function CounterfactualSection({ items }: { items: Counterfactual[] }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;

  return (
    <div style={{ marginTop: '16px' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          background: 'none',
          border: `1px solid ${NAVY}`,
          color: NAVY,
          borderRadius: '4px',
          padding: '5px 12px',
          fontSize: '12px',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        {open ? '▾' : '▸'} Counterfactuals ({items.length})
      </button>

      {open && (
        <div style={{ marginTop: '8px' }}>
          {items.map((cf, i) => (
            <div key={i} style={{
              backgroundColor: '#eef4ff',
              border: '1px solid #c7d9f8',
              borderRadius: '6px',
              padding: '10px 14px',
              marginBottom: '8px',
              fontSize: '12px',
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px', color: NAVY }}>
                If keyword <em>"{cf.simulated_keyword}"</em> were added → GL {cf.would_resolve_to}
              </div>
              <div style={{ marginBottom: '4px', color: '#555' }}>
                Affects: <em>{cf.affected_line}</em>
              </div>
              <div style={{ color: '#555' }}>{cf.explanation}</div>
              <div style={{ marginTop: '4px', fontSize: '11px', color: '#888' }}>
                Confidence: {pct(cf.confidence)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Systemic patterns ─────────────────────────────────────────────────────────

function SystemicPatterns({ patterns }: { patterns: string[] }) {
  if (!patterns || patterns.length === 0) return null;
  return (
    <div style={{
      backgroundColor: '#fff8e1',
      border: '1px solid #ffe082',
      borderRadius: '6px',
      padding: '10px 14px',
      marginTop: '12px',
      fontSize: '12px',
    }}>
      <div style={{ fontWeight: 600, marginBottom: '6px', color: '#795548' }}>
        🔁 Systemic Patterns
      </div>
      <ul style={{ margin: 0, paddingLeft: '18px' }}>
        {patterns.map((p, i) => <li key={i} style={{ marginBottom: '4px' }}>{p}</li>)}
      </ul>
    </div>
  );
}

// ── Processing note evaluation ────────────────────────────────────────────────

function NoteEvaluation({ evaluation }: { evaluation: AuditResponse['processing_note_evaluation'] }) {
  if (!evaluation) return null;
  const color  = evaluation.helpful ? '#d4edda' : '#fff3cd';
  const border = evaluation.helpful ? '#c3e6cb' : '#ffe082';
  const icon   = evaluation.helpful ? '✅' : '⚠️';

  return (
    <div style={{
      backgroundColor: color,
      border: `1px solid ${border}`,
      borderRadius: '6px',
      padding: '10px 14px',
      marginTop: '12px',
      fontSize: '12px',
    }}>
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
        {icon} Processing Note: {evaluation.helpful ? 'Helpful' : 'Needs Attention'}
      </div>
      <div>{evaluation.explanation}</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface TroubleshooterDiffViewProps {
  result: AuditResponse;
  /** Called when user clicks "Promote All Suggestions" — parent opens dialog. */
  onPromote: (items: PromotionItem[]) => void;
  /** Whether a promotion is currently in progress (disables button). */
  promoting?: boolean;
}

export default function TroubleshooterDiffView({
  result,
  onPromote,
  promoting = false,
}: TroubleshooterDiffViewProps) {

  const promotable = result.promotion_items?.filter(Boolean) ?? [];

  // Summary counts for the header
  const resolved   = result.audit_lines?.filter(l => l.status === 'resolved' || l.status === 'improved').length ?? 0;
  const unresolved = result.audit_lines?.filter(l => l.status === 'unresolved').length ?? 0;
  const changed    = result.audit_lines?.filter(l => l.status === 'changed').length ?? 0;

  return (
    <div style={{ padding: '12px 16px' }}>

      {/* Overall verdict */}
      {result.overall_verdict && (
        <div style={{
          backgroundColor: '#eef4ff',
          border: '1px solid #c7d9f8',
          borderRadius: '6px',
          padding: '10px 14px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#1a3a6e',
        }}>
          {result.overall_verdict}
        </div>
      )}

      {/* Line count summary chips */}
      {result.audit_lines && result.audit_lines.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
          {resolved > 0 && (
            <span style={{ backgroundColor: GREEN, color: GREEN_T, padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
              {resolved} improved
            </span>
          )}
          {unresolved > 0 && (
            <span style={{ backgroundColor: RED, color: RED_T, padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
              {unresolved} unresolved
            </span>
          )}
          {changed > 0 && (
            <span style={{ backgroundColor: AMBER, color: AMBER_T, padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
              {changed} review needed
            </span>
          )}
        </div>
      )}

      {/* Diff table */}
      {result.audit_lines && result.audit_lines.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Description</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={thStyle}>Original GL</th>
                <th style={thStyle}>Audit GL</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {result.audit_lines.map((line, i) => (
                <DiffRow key={i} line={line} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
          No line items in audit result.
        </div>
      )}

      {/* Supplemental sections */}
      <NoteEvaluation evaluation={result.processing_note_evaluation} />
      <SystemicPatterns patterns={result.systemic_patterns} />
      <CounterfactualSection items={result.counterfactuals} />

      {/* Promote button */}
      {promotable.length > 0 && (
        <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
          <button
            onClick={() => onPromote(promotable)}
            disabled={promoting}
            style={{
              width: '100%',
              backgroundColor: promoting ? '#b0bec5' : NAVY,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: promoting ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s',
            }}
          >
            {promoting
              ? '⏳ Applying…'
              : `🚀 Promote ${promotable.length} Suggestion${promotable.length !== 1 ? 's' : ''} to Vendor Profile`}
          </button>
        </div>
      )}

      {result.no_issues_found && (
        <div style={{
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '6px',
          padding: '14px',
          marginTop: '12px',
          textAlign: 'center',
          color: GREEN_T,
          fontWeight: 600,
          fontSize: '13px',
        }}>
          ✅ No issues found — vendor profile appears correct for this invoice.
        </div>
      )}
    </div>
  );
}
